import { DeleteIcon, LinkIcon } from "@chakra-ui/icons";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  ButtonGroup,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useRef } from "react";
import { BsArchive } from "react-icons/bs";
import { FiThumbsUp } from "react-icons/fi";
import { FormattedMessage, useIntl } from "react-intl";

import { Scenario, useDeleteScenario, useUpdateScenarioStatus } from "@nens/ldo/api-v1";
import { useActionAllowed } from "@nens/ldo/hooks";
import { useClipboard } from "@nens/react/hooks";

interface ActionButtonsProps {
  scenario: Scenario;
}

export function ActionButtons({ scenario }: ActionButtonsProps) {
  const actionAllowed = useActionAllowed(scenario);
  const toast = useToast();
  const intl = useIntl();
  const copy = useClipboard()[1];

  // For the confirm dialog of the delete action
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);

  const updateStatusMutation = useUpdateScenarioStatus(scenario.id);
  const deleteScenarioMutation = useDeleteScenario(scenario.id);

  const doDelete = async () => {
    await deleteScenarioMutation.mutate();
    onClose();
  };

  return (
    <ButtonGroup variant="outline">
      {actionAllowed("delete") ? (
        <>
          <Button
            title={intl.formatMessage({
              id: "buttons.delete_scenario",
              defaultMessage: "Delete scenario",
            })}
            variant="solid"
            colorScheme="red"
            leftIcon={<DeleteIcon />}
            onClick={onOpen}
          >
            {intl.formatMessage({
              id: "buttons.delete_scenario",
              defaultMessage: "Delete scenario",
            })}
          </Button>
          <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}>
            <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogHeader fontSize="lg" fontWeight="bold">
                  {intl.formatMessage({
                    id: "buttons.delete_scenario",
                    defaultMessage: "Delete scenario",
                  })}
                </AlertDialogHeader>

                <AlertDialogBody>
                  {intl.formatMessage({
                    id: "dialog.areyousure",
                    defaultMessage: "Are you are sure? You can't undo this action afterwards.",
                  })}
                </AlertDialogBody>

                <AlertDialogFooter>
                  <Button ref={cancelRef} onClick={onClose}>
                    <FormattedMessage id="buttons.cancel" defaultMessage="Cancel" />
                  </Button>
                  <Button colorScheme="red" onClick={doDelete} ml={3}>
                    <FormattedMessage id="buttons.delete" defaultMessage="Delete" />
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialogOverlay>
          </AlertDialog>
        </>
      ) : null}
      {actionAllowed("check") ? (
        <Button
          title={intl.formatMessage({
            id: "buttons.scenario_correct",
            defaultMessage: "Scenario imported correctly",
          })}
          variant="solid"
          colorScheme="teal"
          leftIcon={<FiThumbsUp />}
          onClick={() => updateStatusMutation.mutate({ status: "import_checked" })}
        >
          {intl.formatMessage({
            id: "buttons.scenario_correct",
            defaultMessage: "Scenario imported correctly",
          })}
        </Button>
      ) : null}
      {actionAllowed("request qc") ? (
        <Button
          title={intl.formatMessage({
            id: "buttons.request_qc",
            defaultMessage: "Request quality check",
          })}
          onClick={() => updateStatusMutation.mutate({ status: "quality_control_requested" })}
        >
          {intl.formatMessage({
            id: "buttons.request_qc",
            defaultMessage: "Request quality check",
          })}
        </Button>
      ) : null}
      {actionAllowed("archive") ? (
        <Button
          title={intl.formatMessage({ id: "buttons.archive", defaultMessage: "Archive" })}
          leftIcon={<BsArchive />}
          onClick={() => updateStatusMutation.mutate({ status: "archived" })}
        >
          {intl.formatMessage({ id: "buttons.archive", defaultMessage: "Archive" })}
        </Button>
      ) : null}
      {actionAllowed("unarchive") ? (
        <Button
          title={intl.formatMessage({ id: "buttons.unarchive", defaultMessage: "Unarchive" })}
          leftIcon={<BsArchive />}
          onClick={() => updateStatusMutation.mutate({ status: "import_checked" })}
        >
          {intl.formatMessage({ id: "buttons.unarchive", defaultMessage: "Unarchive" })}
        </Button>
      ) : null}
      <Button
        title={intl.formatMessage({
          id: "buttons.link_to_this",
          defaultMessage: "Link to this page",
        })}
        onClick={() => {
          const { href } = window.location;
          copy(href);
          toast({
            title: intl.formatMessage({
              id: "detailpage.copy_to_clipboard.title",
              defaultMessage: "Copied to clipboard",
            }),
            description: intl.formatMessage({
              id: "detailpage.copy_to_clipboard.description",
              defaultMessage: "The link of this page was copied to your clipboard",
            }),
            status: "success",
            duration: 5000,
            isClosable: true,
          });
        }}
      >
        <LinkIcon />
      </Button>
    </ButtonGroup>
  );
}
